/*
 * DropdownMenu: a menu of actions behind a trigger (row actions, the user menu).
 *   <DropdownMenu trigger={<IconButton label=… icon={<DotsThree />} />} items={[
 *     { label: t('actions.edit'), icon: <PencilSimple />, onSelect: edit },
 *     { type: 'separator' },
 *     { label: t('actions.delete'), icon: <Trash />, danger: true, onSelect: confirmDelete },
 *   ]} />
 * Items with `to` render as router links.
 */
import { DropdownMenu as M } from 'radix-ui'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { cn } from '@/lib/cn'

export type MenuItem =
  | {
      type?: 'item'
      label: ReactNode
      icon?: ReactNode
      onSelect?: () => void
      to?: string
      href?: string
      danger?: boolean
      disabled?: boolean
      shortcut?: ReactNode
    }
  | { type: 'separator' }
  | { type: 'label'; label: ReactNode }
  | { type: 'custom'; node: ReactNode }

export const menuContentClasses =
  'z-50 min-w-52 overflow-hidden rounded-2xl bg-surface p-1.5 shadow-pop ring-1 ring-hairline data-[state=closed]:animate-fade-out data-[state=open]:animate-pop-in'
export const menuItemClasses =
  'flex min-h-9 cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-14 text-ink outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-surface-tint touch:min-h-11 [&_svg]:size-[18px] [&_svg]:shrink-0 [&_svg]:text-subtle'

export function DropdownMenu({
  trigger,
  items,
  align = 'end',
  header,
  className,
}: {
  trigger: ReactNode
  items: MenuItem[]
  align?: 'start' | 'end' | 'center'
  header?: ReactNode
  className?: string
}) {
  return (
    <M.Root modal={false}>
      <M.Trigger asChild>{trigger}</M.Trigger>
      <M.Portal>
        <M.Content
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cn(menuContentClasses, className)}
        >
          {header}
          {items.map((item, i) => {
            if (item.type === 'separator')
              return <M.Separator key={i} className="mx-1 my-1.5 h-px bg-hairline" />
            if (item.type === 'label')
              return (
                <M.Label key={i} className="px-2.5 pt-2 pb-1 text-12 font-semibold text-subtle">
                  {item.label}
                </M.Label>
              )
            if (item.type === 'custom') return <div key={i}>{item.node}</div>
            const content = (
              <>
                {item.icon}
                <span className="flex-1">{item.label}</span>
                {item.shortcut ? <span className="text-12 text-subtle">{item.shortcut}</span> : null}
              </>
            )
            const cls = cn(
              menuItemClasses,
              item.danger && 'text-danger-700 data-[highlighted]:bg-danger-50 [&_svg]:text-danger-600',
            )
            if (item.to)
              return (
                <M.Item key={i} asChild disabled={item.disabled} className={cls}>
                  <Link to={item.to}>{content}</Link>
                </M.Item>
              )
            if (item.href)
              return (
                <M.Item key={i} asChild disabled={item.disabled} className={cls}>
                  <a href={item.href} target="_blank" rel="noreferrer">
                    {content}
                  </a>
                </M.Item>
              )
            return (
              <M.Item key={i} disabled={item.disabled} onSelect={item.onSelect} className={cls}>
                {content}
              </M.Item>
            )
          })}
        </M.Content>
      </M.Portal>
    </M.Root>
  )
}
